import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableFooter,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Typography,
  Button,
} from "@mui/material";

const PAIR_BUTTON_COLORS = {
  release: {
    color: "#1976d2",
    borderColor: "#1976d2",
    hoverBorderColor: "#1565c0",
  }, // 対確定解除（青）
  cancel: {
    color: "#757575",
    borderColor: "#9e9e9e",
    hoverBorderColor: "#757575",
  }, // 解除取消（グレー）
};
const pairActionButtonSx = (variant) => {
  const colors = PAIR_BUTTON_COLORS[variant];
  const { color, borderColor, hoverBorderColor } = colors ?? {
    color: "#000",
    borderColor: "#000",
    hoverBorderColor: "#000",
  };
  return {
    whiteSpace: "nowrap",
    color,
    borderColor,
    backgroundColor: "white",
    borderRadius: "20px",
    fontSize: "0.9rem",
    padding: "6px 20px",
    "&:hover": {
      borderColor: hoverBorderColor ?? borderColor,
      backgroundColor: "#f5f5f5",
    },
  };
};

class PairTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      editingId: null,
      editedData: {},
      releasedOkIndices: [], // 解除済みOKペアのインデックス
      releasedOkSnapshot: {}, // 解除時点の元のofPath/ofOrderNumberを保持（キャンセル復元用）
    };
  }

  handleEdit(purchase_no, key, value) {
    const editedData = { ...this.state.editedData };
    editedData[purchase_no] = { ...editedData[purchase_no], [key]: value };
    this.setState({ editedData });
  }

  handleSave(unknown_of) {
    const { editedData } = this.state;
    const newData = this.props.formPairList.map((formPairList) => {
      if (formPairList.unknown_of[0] === unknown_of) {
        return { ...formPairList, ...editedData[unknown_of] };
      }
      return formPairList;
    });
    this.setState({ editingId: null, editedData: {} });
    this.props.onSave(newData);
  }

  // 対確定解除：データは移動せず、stateで管理
  handleReleasePair(index) {
    // クリック時点の値をローカル変数に退避して、setStateの実行タイミングでの値変更を回避
    const oks = this.props.formPairList.oks;
    const originalOfPath = oks && oks[index] ? oks[index].ok.ofPath : null;
    const originalOfOrderNumber =
      oks && oks[index] ? oks[index].ok.ofOrderNumber || "" : "";

    this.setState((prevState) => {
      if (prevState.releasedOkIndices.includes(index)) {
        return null;
      }
      return {
        releasedOkIndices: [...prevState.releasedOkIndices, index],
        releasedOkSnapshot: {
          ...prevState.releasedOkSnapshot,
          [index]: {
            ofPath: originalOfPath,
            ofOrderNumber: originalOfOrderNumber,
          },
        },
      };
    });
  }

  getSwappableOfs() {
    const ofs = [];
    const { oks, ngOfs } = this.props.formPairList;

    if (oks) {
      this.state.releasedOkIndices.forEach((i) => {
        if (oks[i]) {
          ofs.push({
            path: oks[i].ok.ofPath,
            ofOrderNumber: oks[i].ok.ofOrderNumber || "",
          });
        }
      });
    }

    if (ngOfs) {
      ngOfs.forEach((of) => {
        ofs.push({
          path: of.path,
          ofOrderNumber: of.ofOrderNumber || "",
        });
      });
    }

    // ファイル名でソート（常に一定の順序を保つ）
    ofs.sort((a, b) => {
      const nameA = decodeURIComponent(a.path.split("/").pop());
      const nameB = decodeURIComponent(b.path.split("/").pop());
      return nameA.localeCompare(nameB, "ja");
    });

    return ofs;
  }

  handleUnifiedOfSwap(sourceType, sourceIndex, selectedOfPath) {
    const formPairList = this.props.formPairList;
    const newOks = formPairList.oks
      ? JSON.parse(JSON.stringify(formPairList.oks))
      : [];
    const newNgOfs = formPairList.ngOfs
      ? JSON.parse(JSON.stringify(formPairList.ngOfs))
      : [];

    let currentOfPath;
    let currentOfOrderNumber;
    if (sourceType === "ng") {
      currentOfPath = newNgOfs[sourceIndex].path;
      currentOfOrderNumber = newNgOfs[sourceIndex].ofOrderNumber || "";
    } else {
      currentOfPath = newOks[sourceIndex].ok.ofPath;
      currentOfOrderNumber = newOks[sourceIndex].ok.ofOrderNumber || "";
    }

    if (currentOfPath === selectedOfPath) return;

    let selectedOfOrderNumber = "";
    let swapped = false;

    // ngOfs 内を探す
    for (let i = 0; i < newNgOfs.length; i++) {
      if (newNgOfs[i].path === selectedOfPath) {
        selectedOfOrderNumber = newNgOfs[i].ofOrderNumber || "";
        if (sourceType === "ng") {
          newNgOfs[i].path = currentOfPath;
          newNgOfs[i].ofOrderNumber = currentOfOrderNumber;
          newNgOfs[sourceIndex].path = selectedOfPath;
          newNgOfs[sourceIndex].ofOrderNumber = selectedOfOrderNumber;
        } else {
          newNgOfs[i].path = currentOfPath;
          newNgOfs[i].ofOrderNumber = currentOfOrderNumber;
          newOks[sourceIndex].ok.ofPath = selectedOfPath;
          newOks[sourceIndex].ok.ofOrderNumber = selectedOfOrderNumber;
        }
        swapped = true;
        break;
      }
    }

    // 解除済みOKペア内を探す
    if (!swapped) {
      for (const i of this.state.releasedOkIndices) {
        if (newOks[i] && newOks[i].ok.ofPath === selectedOfPath) {
          selectedOfOrderNumber = newOks[i].ok.ofOrderNumber || "";
          if (sourceType === "ng") {
            newOks[i].ok.ofPath = currentOfPath;
            newOks[i].ok.ofOrderNumber = currentOfOrderNumber;
            newNgOfs[sourceIndex].path = selectedOfPath;
            newNgOfs[sourceIndex].ofOrderNumber = selectedOfOrderNumber;
          } else {
            newOks[i].ok.ofPath = currentOfPath;
            newOks[i].ok.ofOrderNumber = currentOfOrderNumber;
            newOks[sourceIndex].ok.ofPath = selectedOfPath;
            newOks[sourceIndex].ok.ofOrderNumber = selectedOfOrderNumber;
          }
          swapped = true;
          break;
        }
      }
    }

    if (!swapped) return;

    const newFormPairList = {
      ...formPairList,
      oks: newOks,
      ngOfs: newNgOfs,
    };
    this.props.onSave(newFormPairList);
  }

  // 対確定解除のキャンセル：元の確定ペアに戻す
  handleCancelReleasePair(index) {
    const { releasedOkSnapshot } = this.state;
    const snapshot = releasedOkSnapshot[index] || {};
    const originalOfPath = snapshot.ofPath;
    const originalOfOrderNumber = snapshot.ofOrderNumber || "";
    const formPairList = this.props.formPairList;
    const currentOfPath =
      formPairList.oks && formPairList.oks[index]
        ? formPairList.oks[index].ok.ofPath
        : null;
    const currentOfOrderNumber =
      formPairList.oks && formPairList.oks[index]
        ? formPairList.oks[index].ok.ofOrderNumber || ""
        : "";

    // 入れ替え済み（ofPathが変わっている）なら、相手側と元に戻す
    if (originalOfPath && currentOfPath && originalOfPath !== currentOfPath) {
      const newOks = JSON.parse(JSON.stringify(formPairList.oks || []));
      const newNgOfs = JSON.parse(JSON.stringify(formPairList.ngOfs || []));

      // 今このindexが持っているof(currentOfPath)を、元の持ち主(originalOfPath)を持つ相手へ返す
      let swapped = false;

      // ngOfs 側に元のofPathがあるか
      for (let i = 0; i < newNgOfs.length; i++) {
        if (newNgOfs[i].path === originalOfPath) {
          newNgOfs[i].path = currentOfPath;
          newNgOfs[i].ofOrderNumber = currentOfOrderNumber;
          newOks[index].ok.ofPath = originalOfPath;
          newOks[index].ok.ofOrderNumber = originalOfOrderNumber;
          swapped = true;
          break;
        }
      }

      // 解除済みOKペア側に元のofPathがあるか
      if (!swapped) {
        for (const i of this.state.releasedOkIndices) {
          if (
            i !== index &&
            newOks[i] &&
            newOks[i].ok.ofPath === originalOfPath
          ) {
            newOks[i].ok.ofPath = currentOfPath;
            newOks[i].ok.ofOrderNumber = currentOfOrderNumber;
            newOks[index].ok.ofPath = originalOfPath;
            newOks[index].ok.ofOrderNumber = originalOfOrderNumber;
            swapped = true;
            break;
          }
        }
      }

      if (!swapped) {
        return; // 復元失敗時は状態を維持して早期 return
      }

      this.props.onSave({
        ...formPairList,
        oks: newOks,
        ngOfs: newNgOfs,
      });
    }

    // state から解除状態とスナップショットを取り除く
    this.setState((prevState) => {
      const newSnapshot = { ...prevState.releasedOkSnapshot };
      delete newSnapshot[index];
      return {
        releasedOkIndices: prevState.releasedOkIndices.filter(
          (i) => i !== index
        ),
        releasedOkSnapshot: newSnapshot,
      };
    });
  }

  renderTableHeader() {
    return (
      <TableHead>
        <TableRow>
          <TableCell>
            <Typography sx={{ fontSize: "1rem" }}>購入申請書</Typography>
          </TableCell>
          <TableCell>
            <Typography sx={{ fontSize: "1rem" }}>注文書類</Typography>
          </TableCell>
          <TableCell>
            <Typography sx={{ fontSize: "1rem" }}>操作</Typography>
          </TableCell>
          <TableCell /> {/* 空のセルを追加 */}
        </TableRow>
      </TableHead>
    );
  }

  renderTableFooter() {
    const hasNg =
      this.props.formPairList.ngPos && this.props.formPairList.ngPos.length > 0;
    return (
      <TableFooter>
        <TableRow>
          <TableCell colSpan={4}>
            <Typography sx={{ fontSize: "0.8rem" }}>
              {hasNg && (
                <>
                  購入申請書と注文書類の対が特定できなかった書類があります。
                  <br />
                  対が特定できなかった注文書類を選択肢から選択してください。
                  <br />
                </>
              )}
              確定された対に間違いがある場合は「対確定解除」し選択し直してください。
            </Typography>
          </TableCell>
        </TableRow>
      </TableFooter>
    );
  }

  renderNgTableData() {
    if (
      !this.props.formPairList.ngPos ||
      this.props.formPairList.ngPos.length === 0
    ) {
      return null;
    }

    const swappableOfs = this.getSwappableOfs(); // ★ 統一プール

    return this.props.formPairList.ngPos.map((po, index) => {
      const poFileName = decodeURIComponent(po.path.split("/").pop());
      const poQuoteNumber = po.poQuoteNumber || ""; // 見積番号がない場合のデフォルト値
      const currentOfPath = this.props.formPairList.ngOfs[index].path;
      const ofFileName = decodeURIComponent(currentOfPath.split("/").pop());
      const ofOrderNumber =
        this.props.formPairList.ngOfs[index].ofOrderNumber || ""; // 注文番号がない場合のデフォルト値

      return (
        <TableRow key={po.path} sx={{ minHeight: "56px" }}>
          {/* 1列目：購入申請書 */}
          <TableCell>
            <a href={po.path} target="_blank" rel="noopener noreferrer">
              {poFileName}
            </a>
            <br />
            {poQuoteNumber && (
              <Typography sx={{ fontSize: "0.75rem", color: "#666" }}>
                見積番号：{poQuoteNumber}
              </Typography>
            )}
          </TableCell>
          {/* 2列目：注文書類 */}
          <TableCell
            sx={{
              paddingLeft: "0px",
              width: "1%",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "4px",
              }}
            >
              <TextField
                value={currentOfPath}
                select
                onChange={(e) =>
                  this.handleUnifiedOfSwap("ng", index, e.target.value)
                }
                sx={{ minWidth: "180px", flexShrink: 0 }}
              >
                {/* ★ ngOfs だけでなく統一プールを使用 */}
                {swappableOfs.map((of) => {
                  const fileName = decodeURIComponent(of.path.split("/").pop());
                  const label = of.ofOrderNumber
                    ? `注文番号：${of.ofOrderNumber} (${fileName})`
                    : fileName;
                  return (
                    <MenuItem key={of.path} value={of.path}>
                      {label}
                    </MenuItem>
                  );
                })}
              </TextField>
              <a
                href={this.props.formPairList.ngOfs[index].path}
                target="_blank"
                rel="noopener noreferrer"
                style={{ whiteSpace: "nowrap" }}
              >
                {ofFileName}
              </a>
            </div>
            {ofOrderNumber && (
              <Typography sx={{ fontSize: "0.75rem", color: "#666" }}>
                注文番号：{ofOrderNumber}
              </Typography>
            )}
          </TableCell>
          {/* 3列目：操作 ※未使用 */}
          <TableCell />
          {/* 4列目：空のセル */}
          <TableCell />
        </TableRow>
      );
    });
  }

  renderOKTableData() {
    if (!this.props.formPairList.oks) return null;

    const swappableOfs = this.getSwappableOfs();

    return this.props.formPairList.oks.map((pair, index) => {
      const poFileName = decodeURIComponent(pair.ok.poPath.split("/").pop());
      const ofFileName = decodeURIComponent(pair.ok.ofPath.split("/").pop());
      const poQuoteNumber = pair.ok.poQuoteNumber || ""; // ★ oks は pair.ok 配下
      const ofOrderNumber = pair.ok.ofOrderNumber || ""; // ★
      const isReleased = this.state.releasedOkIndices.includes(index);

      // 1列目（購入申請書）共通スタイル
      const poCellSx = {
        whiteSpace: "nowrap", // ファイル名で改行させず内容幅に収める
        width: "1%", // 内容幅まで縮める
        paddingRight: "50px", // ファイル名の右に50pxの余白
      };

      if (isReleased) {
        // 解除済み → 2列目にドロップダウン、3列目に「解除取消」
        return (
          <TableRow key={pair.ok.poPath} sx={{ minHeight: "80px" }}>
            {/* 1列目：購入申請書 */}
            <TableCell sx={poCellSx}>
              <a
                href={pair.ok.poPath}
                target="_blank"
                rel="noopener noreferrer"
              >
                {poFileName}
              </a>
              <br />
              {poQuoteNumber && (
                <Typography sx={{ fontSize: "0.75rem", color: "#666" }}>
                  見積番号：{poQuoteNumber}
                </Typography>
              )}
            </TableCell>
            {/* 2列目：注文書類（再選択ドロップダウン） */}
            <TableCell sx={{ paddingLeft: "0px", width: "1%" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "4px",
                }}
              >
                <TextField
                  value={pair.ok.ofPath}
                  select
                  sx={{ minWidth: "180px", flexShrink: 0 }}
                  onChange={(e) =>
                    this.handleUnifiedOfSwap("ok", index, e.target.value)
                  }
                >
                  {swappableOfs.map((of) => {
                    const fileName = decodeURIComponent(
                      of.path.split("/").pop()
                    );
                    const label = of.ofOrderNumber
                      ? `注文番号：${of.ofOrderNumber} (${fileName})`
                      : fileName;
                    return (
                      <MenuItem key={of.path} value={of.path}>
                        {label}
                      </MenuItem>
                    );
                  })}
                </TextField>
                <a
                  href={pair.ok.ofPath}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ whiteSpace: "nowrap" }}
                >
                  {ofFileName}
                </a>
              </div>
              {ofOrderNumber && (
                <Typography sx={{ fontSize: "0.75rem", color: "#666" }}>
                  注文番号：{ofOrderNumber}
                </Typography>
              )}
            </TableCell>
            {/* 3列目：操作 */}
            <TableCell sx={{ paddingLeft: "20px", whiteSpace: "nowrap" }}>
              {/* 解除取消：枠線 グレー */}
              <Button
                variant="outlined"
                size="small"
                onClick={() => this.handleCancelReleasePair(index)}
                sx={pairActionButtonSx("cancel")}
              >
                解除取消
              </Button>
            </TableCell>
            {/* 4列目：空のセル */}
            <TableCell />
          </TableRow>
        );
      }

      // 未解除 → 2列目にファイル名、3列目に「対確定解除」
      return (
        <TableRow key={pair.ok.poPath} sx={{ minHeight: "56px" }}>
          {/* 1列目：購入申請書 */}
          <TableCell sx={poCellSx}>
            <a href={pair.ok.poPath} target="_blank" rel="noopener noreferrer">
              {poFileName}
            </a>
            <br />
            {poQuoteNumber && (
              <Typography sx={{ fontSize: "0.75rem", color: "#666" }}>
                見積番号：{poQuoteNumber}
              </Typography>
            )}
          </TableCell>
          {/* 2列目：注文書類 */}
          <TableCell
            sx={{ paddingLeft: "0px", whiteSpace: "nowrap", width: "1%" }}
          >
            <a href={pair.ok.ofPath} target="_blank" rel="noopener noreferrer">
              {ofFileName}
            </a>
            <br />
            {ofOrderNumber && (
              <Typography sx={{ fontSize: "0.75rem", color: "#666" }}>
                注文番号：{ofOrderNumber}
              </Typography>
            )}
          </TableCell>
          {/* 3列目：操作 */}
          <TableCell sx={{ paddingLeft: "20px", whiteSpace: "nowrap" }}>
            {/* 対確定解除：枠線 青 */}
            <Button
              variant="outlined"
              size="small"
              onClick={() => this.handleReleasePair(index)}
              sx={pairActionButtonSx("release")}
            >
              対確定解除
            </Button>
          </TableCell>
          {/* 4列目：空のセル */}
          <TableCell />
        </TableRow>
      );
    });
  }
  render() {
    return (
      <TableContainer component={Paper} style={{ backgroundColor: "#f2f2f2" }}>
        <Table>
          {this.renderTableHeader()}
          <TableBody>
            {this.renderNgTableData()}
            {this.renderOKTableData()}
          </TableBody>
          {this.renderTableFooter()}
        </Table>
      </TableContainer>
    );
  }
}

export default PairTable;
